import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, FileText, Trash2, Loader2,
  Globe, Clock, Archive, File, Pencil, Check, X, Eye, Copy, RotateCcw,
} from 'lucide-react';
import { pageBuilderApi } from '@/lib/api';
import { withAdminPreviewParam } from '@/lib/adminPreview';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ContentStatusTabs, useContentStatusFilter } from './ContentStatusTabs';
import type { PageModel, PageStatus } from '@/types/page-builder.types';

const STATUS_CONFIG: Record<PageStatus, { label: string; color: string; icon: typeof Globe }> = {
  DRAFT: { label: 'Szkic', color: 'text-warning bg-yellow-400/10', icon: File },
  PUBLISHED: { label: 'Opublikowany', color: 'text-success bg-success/10', icon: Globe },
  SCHEDULED: { label: 'Zaplanowany', color: 'text-accent-info bg-blue-400/10', icon: Clock },
  TRASHED: { label: 'W koszu', color: 'text-text-muted bg-surface-muted', icon: Trash2 },
  ARCHIVED: { label: 'Zarchiwizowany', color: 'text-text-secondary bg-gray-400/10', icon: Archive },
};

function titleToSlug(title: string) {
  return title
    .toLowerCase()
    .replace(/[ąĄ]/g, 'a').replace(/[ćĆ]/g, 'c').replace(/[ęĘ]/g, 'e')
    .replace(/[łŁ]/g, 'l').replace(/[ńŃ]/g, 'n').replace(/[óÓ]/g, 'o')
    .replace(/[śŚ]/g, 's').replace(/[źŹżŻ]/g, 'z')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 100);
}

export function KreatorAdvertorialsSection({ wordpressUrl = '' }: { wordpressUrl?: string }) {
  const { integrationId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status: statusFilter, setStatus, pageStatus } = useContentStatusFilter();

  // New advertorial form
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSlug, setEditSlug] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugManuallyEdited) {
      setNewSlug(titleToSlug(newTitle));
    }
  }, [newTitle, slugManuallyEdited]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['page-builder-advertorials', integrationId, pageStatus],
    queryFn: () => pageBuilderApi.listPages(integrationId, { pageType: 'ADVERTORIAL', ...(pageStatus ? { status: pageStatus } : {}) }),
    enabled: !!integrationId,
  });

  const createMutation = useMutation({
    mutationFn: (data: { title: string; slug: string; pageType: string }) =>
      pageBuilderApi.createPage(integrationId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['page-builder-advertorials', integrationId] });
      setShowNew(false);
      setNewTitle('');
      setNewSlug('');
      setSlugManuallyEdited(false);
      navigate(`/kreator/${integrationId}/builder/${response.data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ pageId, data }: { pageId: string; data: Record<string, unknown> }) =>
      pageBuilderApi.updatePage(integrationId, pageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-builder-advertorials', integrationId] });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (pageId: string) => pageBuilderApi.deletePage(integrationId, pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-builder-advertorials', integrationId] });
      setDeleteTarget(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (pageId: string) => pageBuilderApi.restorePage(integrationId, pageId),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['page-builder-advertorials', integrationId] }); },
  });

  const duplicateMutation = useMutation({
    mutationFn: (pageId: string) => pageBuilderApi.duplicatePage(integrationId, pageId),
    onMutate: (pageId) => {
      setDuplicatingId(pageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-builder-advertorials', integrationId] });
    },
    onSettled: () => {
      setDuplicatingId(null);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ pageId, newStatus }: { pageId: string; newStatus: PageStatus }) =>
      pageBuilderApi.updatePage(integrationId, pageId, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-builder-advertorials', integrationId] });
    },
  });

  const pages = (data?.data ?? []) as PageModel[];

  const handleCreate = () => {
    const title = newTitle.trim();
    if (!title) return;
    const slug = newSlug.trim() || titleToSlug(title);
    if (!slug) return;
    createMutation.mutate({ title, slug, pageType: 'ADVERTORIAL' });
  };

  const handleStartEdit = (page: PageModel, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(page.id);
    setEditTitle(page.title);
    setEditSlug(page.slug);
  };

  const handleSaveEdit = (pageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editTitle.trim() || !editSlug.trim()) return;
    updateMutation.mutate({ pageId, data: { title: editTitle.trim(), slug: editSlug.trim() } });
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(null);
  };

  const handleDelete = (pageId: string, pageTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ id: pageId, title: pageTitle });
  };

  const handleToggleStatus = (page: PageModel, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus: PageStatus = page.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    toggleStatusMutation.mutate({ pageId: page.id, newStatus });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <FileText className="w-5 h-5 text-accent-primary" />
            Advertoriale
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Strony advertorialowe budowane w Builder v2 - widoczne na WordPress pod swoim slug-em.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Nowy advertorial
        </button>
      </div>

      {/* New advertorial form */}
      {showNew && (
        <div className="mb-6 p-4 rounded-xl bg-surface-raised border border-border">
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-secondary mb-1">Tytuł advertorialu</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-sm"
                placeholder="np. 5 sposobów na lepszy sen..."
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>

            <div>
              <label className="block text-xs text-text-secondary mb-1">
                Slug (adres URL: twojsklep.pl/<span className="text-accent-primary">{newSlug || '...'}</span>)
              </label>
              <input
                type="text"
                value={newSlug}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                }}
                className="w-full px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-sm font-mono"
                placeholder="np. 5-sposobow-na-lepszy-sen"
              />
            </div>

            <div className="flex items-center gap-2">
              <button onClick={handleCreate} disabled={createMutation.isPending || !newTitle.trim()} className="btn-primary text-sm">
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Utwórz'}
              </button>
              <button onClick={() => { setShowNew(false); setNewTitle(''); setNewSlug(''); setSlugManuallyEdited(false); }} className="btn-secondary text-sm">
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      <ContentStatusTabs value={statusFilter} onChange={setStatus} />
      {(isError || deleteMutation.isError || restoreMutation.isError || duplicateMutation.isError || toggleStatusMutation.isError) && <p role="alert" className="text-sm text-danger mb-4">Nie udało się wykonać operacji. Spróbuj ponownie.</p>}
      {/* Pages list */}
      {pages.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={statusFilter === 'trash' ? 'Kosz jest pusty' : 'Brak advertoriali'}
          description="Brak advertoriali dla wybranego statusu."
          buttonLabel="Nowy advertorial"
          onButtonClick={() => setShowNew(true)}
          buttonIcon={Plus}
        />
      ) : (
        <div className="space-y-2">
          {pages.map((page) => {
            const status = STATUS_CONFIG[page.status] ?? STATUS_CONFIG.DRAFT;
            const StatusIcon = status.icon;
            const isEditing = editingId === page.id;

            return (
              <div
                key={page.id}
                className="flex items-center justify-between p-4 rounded-xl bg-surface-raised border border-border hover:border-border-strong cursor-pointer transition-colors"
                onClick={() => page.status !== 'TRASHED' && !isEditing && navigate(`/kreator/${integrationId}/builder/${page.id}`)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-text-secondary shrink-0" />

                  {isEditing ? (
                    <div className="flex-1 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full px-2 py-1 bg-surface-hover border border-border rounded text-sm"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(page.id, e as unknown as React.MouseEvent)}
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-text-muted">slug:</span>
                        <input
                          type="text"
                          value={editSlug}
                          onChange={(e) => setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                          className="flex-1 px-2 py-0.5 bg-surface-hover border border-border rounded text-xs font-mono"
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(page.id, e as unknown as React.MouseEvent)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{page.title}</div>
                      <div className="text-xs text-text-muted font-mono truncate">/{page.slug}</div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 shrink-0 ml-3">
                  {page.status === 'TRASHED' ? <button className="btn-secondary text-xs flex items-center gap-2" disabled={restoreMutation.isPending} onClick={event => { event.stopPropagation(); restoreMutation.mutate(page.id); }}><RotateCcw className="w-4 h-4" />Przywróć jako szkic</button> : <>
                  {/* Publish toggle */}
                  <button
                    onClick={(e) => handleToggleStatus(page, e)}
                    disabled={toggleStatusMutation.isPending}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors hover:opacity-80 ${status.color}`}
                    title={page.status === 'PUBLISHED' ? 'Kliknij aby cofnąć do szkicu' : 'Kliknij aby opublikować'}
                  >
                    <StatusIcon className="w-3 h-3" />
                    {status.label}
                  </button>

                  {page.status === 'PUBLISHED' && wordpressUrl && (
                    <a
                      href={withAdminPreviewParam(`${wordpressUrl.replace(/\/+$/, '')}/${page.slug}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 hover:bg-accent-primary/10 rounded-lg text-text-secondary hover:text-accent-primary transition-colors"
                      title="Podejrzyj na stronie"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </a>
                  )}

                  <span className="text-xs text-text-muted">
                    {new Date(page.updatedAt).toLocaleDateString('pl-PL')}
                  </span>

                  {isEditing ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => handleSaveEdit(page.id, e)}
                        disabled={updateMutation.isPending}
                        className="p-1.5 hover:bg-success/10 rounded-lg text-success transition-colors"
                        title="Zapisz"
                      >
                        {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1.5 hover:bg-surface-hover/60 rounded-lg text-text-secondary transition-colors"
                        title="Anuluj"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateMutation.mutate(page.id);
                        }}
                        disabled={duplicatingId === page.id}
                        className="p-1.5 hover:bg-accent-primary/10 rounded-lg text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50"
                        title="Duplikuj jako szkic"
                      >
                        {duplicatingId === page.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={(e) => handleStartEdit(page, e)}
                        className="p-1.5 hover:bg-surface-hover/60 rounded-lg text-text-secondary hover:text-text-primary transition-colors"
                        title="Edytuj nazwę i slug"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  <button
                    onClick={(e) => handleDelete(page.id, page.title, e)}
                    className="p-1.5 hover:bg-danger/10 rounded-lg text-text-secondary hover:text-danger transition-colors"
                    title="Przenieś do kosza"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info */}
      <div className="mt-6 text-[11px] text-text-disabled">
        Wyświetlane: {pages.length} {pages.length === 1 ? 'advertorial' : pages.length < 5 ? 'advertoriale' : 'advertoriali'}
      </div>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Przenieść advertorial do kosza?"
        message={
          <p>
            Czy na pewno chcesz przenieść do kosza advertorial <span className="font-medium text-text-primary">„{deleteTarget?.title}"</span>?
          </p>
        }
        subtitle="Advertorial możesz później przywrócić jako szkic."
        description="Treść i wszystkie wersje zostaną zachowane. Advertorial przestanie być dostępny na sklepie."
        confirmLabel="Przenieś do kosza"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
